import { Component, computed, inject, input, LOCALE_ID, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LookupItem, NotFulfilledRow } from './item-status-table.model';
import { Stepper } from '../../../../../../shared/ui/stepper/stepper';
import { CloseIcon } from '../../../../../../shared/ui/icons/package-order/close-icon/close-icon';
import { PlusIcon } from '../../../../../../shared/ui/icons/package-order/plus-icon/plus-icon';
import { ChevronDownIcon } from '../../../../../../shared/ui/icons/package-order/chevron-down-icon/chevron-down-icon';

@Component({
  selector: 'app-item-status-table',
  standalone: true,
  imports: [CommonModule, FormsModule, Stepper, CloseIcon, PlusIcon, ChevronDownIcon],
  templateUrl: './item-status-table.html',
})
export class ItemStatusTable {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));

  orderedQuantity = input.required<number>();
  notFulfilledRows = input<NotFulfilledRow[]>([]);
  reasons = input<LookupItem[]>([]);
  fulfilledLabel = input<string>('Packaged');
  notFulfilledLabel = input<string>('Not Packaged');
  disabled = input<boolean>(false);

  rowsChange = output<NotFulfilledRow[]>();

  processedReasons = computed(() => {
    const list = this.reasons() || [];
    const hasOther = list.some(
      (r) =>
        r.titleEn?.trim().toLowerCase() === 'other' ||
        r.titleAr?.trim() === 'أخرى' ||
        r.titleAr?.trim() === 'اخري',
    );

    if (hasOther) return list;

    return [
      ...list,
      {
        id: -99999,
        titleEn: 'Other',
        titleAr: 'أخرى',
      },
    ];
  });

  otherReasonId = computed(() => {
    const found = this.processedReasons().find(
      (r) =>
        r.titleEn?.trim().toLowerCase() === 'other' ||
        r.titleAr?.trim() === 'أخرى' ||
        r.titleAr?.trim() === 'اخري',
    );
    return found?.id ?? -99999;
  });

  notFulfilledTotal = computed(() => this.notFulfilledRows().reduce((acc, r) => acc + (r.quantity || 0), 0));

  fulfilledQuantity = computed(() => Math.max(0, this.orderedQuantity() - this.notFulfilledTotal()));

  canAddRow = computed(
    () => !this.disabled() && this.fulfilledQuantity() > 0 && this.notFulfilledRows().length < this.orderedQuantity(),
  );

  addRow() {
    if (!this.canAddRow()) return;

    const newRow: NotFulfilledRow = {
      rowId: crypto.randomUUID(),
      quantity: 1,
      reasonId: null,
      otherReason: '',
    };

    this.rowsChange.emit([...this.notFulfilledRows(), newRow]);
  }

  removeRow(rowId: string) {
    if (this.disabled()) return;
    this.rowsChange.emit(this.notFulfilledRows().filter((r) => r.rowId !== rowId));
  }

  maxQtyForRow(rowId: string): number {
    const otherRowsTotal = this.notFulfilledRows()
      .filter((r) => r.rowId !== rowId)
      .reduce((acc, r) => acc + (r.quantity || 0), 0);
    return Math.max(1, this.orderedQuantity() - otherRowsTotal);
  }

  onQuantityInput(rowId: string, rawValue: string | number) {
    if (this.disabled()) return;
    const max = this.maxQtyForRow(rowId);
    let value = typeof rawValue === 'number' ? rawValue : parseInt(rawValue, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (value > max) value = max;

    this.rowsChange.emit(this.notFulfilledRows().map((r) => (r.rowId === rowId ? { ...r, quantity: value } : r)));
  }

  increaseRowQty(rowId: string) {
    if (this.disabled()) return;
    const row = this.notFulfilledRows().find((r) => r.rowId === rowId);
    if (!row) return;
    const max = this.maxQtyForRow(rowId);
    if (row.quantity >= max) return;
    this.onQuantityInput(rowId, row.quantity + 1);
  }

  decreaseRowQty(rowId: string) {
    if (this.disabled()) return;
    const row = this.notFulfilledRows().find((r) => r.rowId === rowId);
    if (!row || row.quantity <= 1) return;
    this.onQuantityInput(rowId, row.quantity - 1);
  }

  onReasonChange(rowId: string, reasonIdRaw: string | number | null) {
    if (this.disabled()) return;
    const reasonId = reasonIdRaw === null || reasonIdRaw === undefined || reasonIdRaw === '' ? null : +reasonIdRaw;
    this.rowsChange.emit(
      this.notFulfilledRows().map((r) =>
        r.rowId === rowId ? { ...r, reasonId, otherReason: reasonId === this.otherReasonId() ? r.otherReason : '' } : r,
      ),
    );
  }

  onOtherReasonInput(rowId: string, value: string) {
    if (this.disabled()) return;
    this.rowsChange.emit(this.notFulfilledRows().map((r) => (r.rowId === rowId ? { ...r, otherReason: value } : r)));
  }
}